import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getSocket } from '../services/socket';

export default function WhotTournamentDetails() {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [tournament, setTournament] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  
  const [seeking, setSeeking] = useState(false);
  const [timeLeftStr, setTimeLeftStr] = useState('');
  
  const seekInterval = useRef(null);
  const clockInterval = useRef(null);

  const loadTournament = async () => {
    try {
      const res = await api.get(`/whot/tournaments/${tournamentId}`);
      setTournament(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load arena details');
    } finally {
      setLoading(false);
    }
  };

  const viewerId = Number(user?.id || 0);
  const isCreator = tournament && Number(tournament.creator_id) === viewerId;
  const isLoggedInPlayer = tournament && tournament.players?.some(p => Number(p.id) === viewerId);
  const me = tournament?.players?.find(p => Number(p.id) === viewerId);

  useEffect(() => {
    loadTournament();
    const refreshIval = setInterval(() => {
      loadTournament();
    }, 7000);
    return () => clearInterval(refreshIval);
  }, [tournamentId, tournament?.status]);

  // Auto seek if active, joined, and idle OR if stuck as 'playing' with no active room.
  // The 'playing+no-room' branch triggers the backend self-healing recovery path.
  useEffect(() => {
    if (tournament?.status !== 'active' || !isLoggedInPlayer || seeking) return;

    if (me?.arena_status === 'idle') {
      setSeeking(true);
    } else if (me?.arena_status === 'playing' && !me?.active_room_id) {
      // Player is stuck: marked 'playing' but no pending match exists.
      // Calling seek will trigger the backend self-healing reset.
      setSeeking(true);
    }
  }, [tournament?.status, isLoggedInPlayer, me?.arena_status, me?.active_room_id, seeking]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    
    const onPaired = (data) => {
      if (String(data.tournamentId) === String(tournamentId) && data.roomId) {
        setSeeking(false);
        navigate(`/whot-online/room/${data.roomId}`);
      }
    };
    socket.on('whot:arena_paired', onPaired);
    return () => {
      socket.off('whot:arena_paired', onPaired);
    };
  }, [tournamentId, navigate]);

  useEffect(() => {
    if (!tournament) return;
    if (tournament.status === 'completed') {
      setTimeLeftStr('Arena Concluded');
      return;
    }
    if (tournament.status === 'pending') {
      setTimeLeftStr('Waiting to Start');
      return;
    }
    if (tournament.status === 'active' && tournament.started_at) {
      if (clockInterval.current) clearInterval(clockInterval.current);
      clockInterval.current = setInterval(() => {
        const startMs = new Date(tournament.started_at).getTime();
        const durMs = Number(tournament.duration_minutes || 30) * 60 * 1000;
        const remaining = (startMs + durMs) - Date.now();
        if (remaining <= 0) {
          setTimeLeftStr('Arena Concluded');
          clearInterval(clockInterval.current);
          loadTournament();
        } else {
          const m = Math.floor(remaining / 60000);
          const s = Math.floor((remaining % 60000) / 1000);
          setTimeLeftStr(`${m}m ${s < 10 ? '0' : ''}${s}s`);
        }
      }, 1000);
    }
    return () => clearInterval(clockInterval.current);
  }, [tournament]);

  useEffect(() => {
    if (seeking) {
      seekInterval.current = setInterval(async () => {
        try {
          const res = await api.post(`/whot/tournaments/${tournamentId}/seek`);
          if (res.data.status === 'playing' || res.data.status === 'paired') {
            clearInterval(seekInterval.current);
            setSeeking(false);
            navigate(`/whot-online/room/${res.data.roomId}?tournamentId=${tournamentId}`);
          }
        } catch (e) {
          console.error('Seek error', e);
        }
      }, 3000);
    } else {
      if (seekInterval.current) clearInterval(seekInterval.current);
    }
    return () => clearInterval(seekInterval.current);
  }, [seeking, tournamentId, navigate]);

  const handleStart = async () => {
    try {
      setLoading(true);
      const res = await api.post(`/whot/tournaments/${tournamentId}/start`);
      setTournament(res.data);
      setLoading(false);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to start arena');
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    try {
      setLoading(true);
      const res = await api.post(`/whot/tournaments/${tournamentId}/join`);
      setTournament(res.data);
      setLoading(false);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to join arena');
      setLoading(false);
    }
  };

  const handleSeek = () => {
    setSeeking(true);
  };
  
  const handleCancelSeek = async () => {
    setSeeking(false);
    try {
      await api.post(`/whot/tournaments/${tournamentId}/unseek`);
    } catch (e) {}
  };

  const handleCancelTournament = async () => {
    if (!window.confirm('Cancel and delete this Arena?')) return;
    try {
      await api.post(`/whot/tournaments/${tournamentId}/cancel`);
      navigate('/whot/tournaments');
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to cancel arena');
    }
  };

  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="dashboard-content">
        <div className="chess-lobby-header">
          <div className="header-left">
            <div className="header-icon">🃏</div>
            <div>
              <h1 className="page-title">{tournament?.name || 'Arena Details'}</h1>
              <p className="page-subtitle">{timeLeftStr}</p>
            </div>
          </div>
          <button 
            className="back-btn" 
            disabled={seeking}
            onClick={() => navigate('/whot/tournaments')}
            style={{ opacity: seeking ? 0.5 : 1, cursor: seeking ? 'not-allowed' : 'pointer' }}
          >
            ← Back to Arenas
          </button>
        </div>

        {error && (
          <div className="error-alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button className="error-close" onClick={() => setError('')}>×</button>
          </div>
        )}

        {loading && <div style={{ color: 'white', marginTop: 24 }}>Loading Arena...</div>}

        {!loading && tournament && (
          <>
            <div className="user-stats-card" style={{ marginBottom: 24, padding: 32, textAlign: 'center', background: 'linear-gradient(135deg, rgba(225, 29, 72, 0.1), rgba(15, 23, 42, 1))' }}>
                <h2 style={{ fontSize: 32, margin: '0 0 16px 0' }}>{timeLeftStr}</h2>
                
                {tournament.status === 'pending' && <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 24 }}>Waiting for the creator to open the Arena.</p>}

                <div style={{ 
                  marginBottom: 24, 
                  padding: '16px 24px', 
                  background: 'rgba(250, 204, 21, 0.1)', 
                  border: '1px solid rgba(250, 204, 21, 0.3)',
                  borderRadius: 16, 
                  display: 'inline-block',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }}>
                  <div style={{ fontSize: 13, color: '#facc15', textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 800, marginBottom: 8 }}>Total Prize Pool</div>
                  <div style={{ fontSize: 32, fontWeight: 900, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                    <span style={{ fontSize: 36 }}>💰</span>
                    {Number(tournament.entry_wager || 0) * Number(tournament.player_count || 0)}
                    <span style={{ fontSize: 16, fontWeight: 600, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase' }}>wkp</span>
                  </div>
                  {Number(tournament.entry_wager) === 0 && (
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>Free Entry Arena</div>
                  )}
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                   {tournament.status === 'pending' && !isLoggedInPlayer && user?.role !== 'admin' && (
                    <button className="flappy-btn-primary" onClick={handleJoin} style={{ padding: '16px 32px', fontSize: 18 }}>
                      Join Arena
                    </button>
                  )}
                  {tournament.status === 'pending' && isCreator && (
                    <button className="flappy-btn-secondary" onClick={handleStart} style={{ padding: '16px 32px', fontSize: 18, background: '#e11d48', borderColor: '#be123c', color: 'white' }}>
                      Start Arena Now
                    </button>
                  )}
                   {tournament.status === 'active' && !isLoggedInPlayer && user?.role !== 'admin' && (
                    <button 
                      className="flappy-btn-primary" 
                      onClick={handleJoin} 
                      style={{ padding: '16px 32px', fontSize: 18, background: '#10b981', borderColor: '#059669', cursor: 'pointer' }}
                    >
                      Join & Play
                    </button>
                  )}
                  {tournament.status === 'active' && isLoggedInPlayer && !seeking && me?.arena_status !== 'playing' && (
                    <button className="flappy-btn-primary" onClick={handleSeek} style={{ padding: '16px 40px', fontSize: 20, background: '#10b981', borderColor: '#059669', animation: 'pulse 2s infinite' }}>
                      Play
                    </button>
                  )}
                  {tournament.status === 'active' && isLoggedInPlayer && seeking && (
                    <div className="flappy-btn-secondary" style={{ padding: '16px 40px', fontSize: 20, opacity: 0.8, cursor: 'default' }}>
                      <span className="seeking-dot"></span> Searching Opponent...
                    </div>
                  )}
                  {tournament.status === 'active' && isLoggedInPlayer && me?.arena_status === 'playing' && me?.active_room_id && (
                    <button 
                      className="flappy-btn-primary" 
                      onClick={() => navigate(`/whot-online/room/${me.active_room_id}?tournamentId=${tournamentId}`)}
                      style={{ padding: '16px 40px', fontSize: 20, background: '#f59e0b', borderColor: '#d97706', animation: 'pulse 2s infinite' }}
                    >
                      ⚔️ YOU ARE IN A MATCH - JOIN BACK
                    </button>
                  )}
                  {!isLoggedInPlayer && tournament.status === 'active' && (
                    <div style={{ padding: '16px 40px', fontSize: 18, color: 'rgba(255,255,255,0.6)' }}>
                       Arena is strictly underway.
                    </div>
                  )}
                  {isCreator && tournament.status === 'pending' && (
                    <button
                      className="flappy-btn-secondary"
                      onClick={handleCancelTournament}
                      style={{ padding: '12px 24px', fontSize: 14, background: '#7f1d1d', borderColor: '#991b1b', color: 'white', opacity: 0.8 }}
                    >
                      🗑 Cancel Arena
                    </button>
                  )}
                </div>
            </div>

            <div style={{ display: 'block' }}>
              <div className="matches-section" style={{ margin: 0 }}>
                <div className="section-header">
                  <h2 className="section-title">Live Leaderboard</h2>
                  <p className="section-subtitle">2 pts per win, 1 pt per draw. Streaks grant +4 pts!</p>
                </div>
                <div className="matches-list">
                  {tournament.players?.map((player, index) => (
                    <div key={player.id} className="match-card chess-tournament-card" style={{ display: 'flex', alignItems: 'center' }}>
                      <div style={{ paddingRight: 20, fontSize: 24, fontWeight: 800, color: index === 0 ? '#facc15' : index === 1 ? '#e2e8f0' : index === 2 ? '#b45309' : 'rgba(255,255,255,0.2)' }}>
                        #{index + 1}
                      </div>
                      <div className="match-card-main" style={{ flex: 1, padding: 0 }}>
                        <div>
                          <div className="match-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {player.username}
                            {Number(player.win_streak) > 1 && <span title={`${player.win_streak} win streak!`} style={{ fontSize: 16 }}>🔥</span>}
                          </div>
                          <div className="match-meta">
                            {player.whot_rating || 1200} ELO • {player.arena_status}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: 24, fontWeight: 700, paddingLeft: 16 }}>
                        {player.score} <span style={{ fontSize: 16, color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>pts</span>
                      </div>
                    </div>
                  ))}
                  {tournament.players?.length === 0 && <div style={{ color: 'rgba(255,255,255,0.6)' }}>No players have joined this arena yet.</div>}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
